
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Crown, User } from 'lucide-react';

interface ManagerSelectorProps {
  teamMembers: Array<{ id: string; name: string; email: string }>;
  selectedManagerId?: string;
  onManagerChange: (managerId: string) => void;
  disabled?: boolean;
}

const ManagerSelector: React.FC<ManagerSelectorProps> = ({
  teamMembers,
  selectedManagerId,
  onManagerChange,
  disabled = false
}) => {
  if (teamMembers.length === 0) return null;

  const selectedManager = teamMembers.find(member => member.id === selectedManagerId);

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Crown className="h-5 w-5 text-purple-600" />
          Select Team Manager
        </CardTitle>
        <p className="text-sm text-gray-600">
          Choose who leads this team for personalized management insights
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Crown className="h-4 w-4" />
            Team Manager
          </label>
          <Select
            value={selectedManagerId || ''}
            onValueChange={onManagerChange}
            disabled={disabled}
          >
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select team manager..." />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              {teamMembers.map(member => (
                <SelectItem key={member.id} value={member.id} className="hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div className="flex flex-col">
                      <span className="font-medium">{member.name}</span>
                      <span className="text-xs text-gray-500">{member.email}</span>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedManager && (
          <div className="flex justify-center pt-2">
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 px-4 py-2">
              <Crown className="h-4 w-4 mr-2" />
              Manager: {selectedManager.name}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManagerSelector;
